import { useDispatch, useSelector } from 'react-redux'
import { VyneTable } from '@vynedental/design-system'
import { Spin } from 'antd'
import moment from 'moment'

import { RootState } from '../../../../appReducer'
import { PracticePatientSort } from '../../../../models/v2/Practice'
import { convertNumCentsToDollars } from '../../../../util/formatCurrency'
import Paginator from '../../../shared/Paginator'
import { patientFinancialDataSelectPatient } from '../v2actions'

import './PatientsTable.sass'

interface Props {
    practiceId: string
    practiceLocationNames: { [key: string]: string }
    numOfPracticeLocations: number
    searchSort: PracticePatientSort | undefined
    searchOrder: 'asc' | 'desc' | undefined
    searchPage: number
    noRecordsFound: boolean
    setSearchSort: (sort: PracticePatientSort) => void
    setSearchOrder: (order: 'asc' | 'desc') => void
    setSearchPage: (page: number) => void
}

const PatientsTable = ({
    practiceId,
    practiceLocationNames,
    numOfPracticeLocations,
    searchOrder,
    searchSort,
    searchPage,
    noRecordsFound,
    setSearchPage,
    setSearchOrder,
    setSearchSort,
}: Props) => {
    const list = useSelector((state: RootState) => state.v2.practicePatients.patients?.[practiceId]?.list || [])
    const pagination = useSelector(
        (state: RootState) => state.v2.practicePatients.patients?.[practiceId]?.pagination || null,
    )
    const loading = useSelector(
        (state: RootState) => state.v2.practicePatients.patients?.[practiceId]?.loading || false,
    )

    const dispatch = useDispatch()

    const handlePageChange = (nextPage: number) => {
        setSearchPage(nextPage)
    }

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        if (sorter && sorter.columnKey && sorter.order) {
            const newOrder = sorter.order === 'ascend' ? 'asc' : 'desc'
            setSearchSort(sorter.columnKey)
            setSearchOrder(newOrder)
        } else if (sorter && !sorter.order) {
            const flipped = searchOrder === 'desc' ? 'asc' : 'desc'
            setSearchOrder(flipped)
        }
    }

    const getSortOrder = (field: string) => {
        if (searchSort !== field) return null
        return searchOrder === 'asc' ? ('ascend' as const) : ('descend' as const)
    }

    const columns = [
        {
            title: 'Last Name',
            dataIndex: 'last_name',
            key: 'last_name',
            width: '20%',
            sorter: true,
            sortOrder: getSortOrder('last_name'),
        },
        {
            title: 'First Name',
            dataIndex: 'first_name',
            key: 'first_name',
            width: '20%',
            sorter: true,
            sortOrder: getSortOrder('first_name'),
        },
        {
            title: 'DOB',
            dataIndex: 'date_of_birth',
            key: 'date_of_birth',
            width: '15%',
            sorter: true,
            sortOrder: getSortOrder('date_of_birth'),
            render: (date: string) => (date ? moment(date).format('MM/DD/YYYY') : ''),
        },
        ...(numOfPracticeLocations > 1
            ? [
                  {
                      title: 'Practice Location',
                      dataIndex: 'practice_location_id',
                      key: 'location_name',
                      width: '20%',
                      sorter: true,
                      sortOrder: getSortOrder('location_name'),
                      render: (locationId: string) =>
                          locationId ? practiceLocationNames[locationId] || locationId : '',
                  },
              ]
            : []),
        {
            title: 'Last Visit',
            dataIndex: 'last_visit',
            key: 'last_visit',
            width: '15%',
            sorter: true,
            sortOrder: getSortOrder('last_visit'),
            render: (date: string) => (date ? moment(date).format('MM/DD/YYYY') : ''),
        },
        {
            title: 'Lifetime Value',
            dataIndex: 'lifetime_value',
            key: 'lifetime_value',
            width: '15%',
            sorter: true,
            sortOrder: getSortOrder('lifetime_value'),
            render: (value: string) => convertNumCentsToDollars(Number(value) * 100, true, '$'),
        },
    ]

    if (list.length === 0 && !noRecordsFound) {
        return (
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    height: '100px',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Spin size="large" />
            </div>
        )
    }

    return (
        <div className="patients-table">
            <VyneTable
                columns={columns}
                dataSource={list}
                rowKey="id"
                loading={loading}
                pagination={false}
                tableLayout="fixed"
                onChange={handleTableChange}
                onRow={patient => ({
                    onClick: () => dispatch(patientFinancialDataSelectPatient(practiceId, patient)),
                    style: { cursor: 'pointer' },
                })}
                dataTestId="patients-table"
            />
            {pagination && (
                <div className="table-pagination">
                    <Paginator currentPage={searchPage} paginationInfo={pagination} selectPage={handlePageChange} />
                </div>
            )}
        </div>
    )
}

export default PatientsTable
